import { Icon } from "@mdi/react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { mdiAlertCircle } from "@mdi/js";
import { Dropdown, Tag, Tooltip } from "antd";

import { Field } from "../../models/mapLayer";
import { VARIABLE_REGEX } from "../utils/layerUtils";
import styles from "./FormulaInput.module.scss";

/** Convert a field name to its expression variable form: "Net Pay" → "$Net_Pay" */
const toVariable = (name: string): string => `$${name.replace(/ /g, "_")}`;

/** Convert an expression variable back to display name: "$Net_Pay" → "Net Pay" */
const toDisplayName = (variable: string): string =>
  variable.replace(/^\$/, "").replace(/_/g, " ");

interface ExpressionToken {
  type: "variable" | "text";
  value: string;
}

/** Parse a raw expression string into an array of variable and text tokens */
const parseExpression = (expression: string): ExpressionToken[] => {
  if (!expression) return [];
  const tokens: ExpressionToken[] = [];
  let lastIndex = 0;

  for (const match of expression.matchAll(VARIABLE_REGEX)) {
    const matchIndex = match.index ?? 0;
    if (matchIndex > lastIndex) {
      tokens.push({ type: "text", value: expression.slice(lastIndex, matchIndex) });
    }
    tokens.push({ type: "variable", value: match[0] });
    lastIndex = matchIndex + match[0].length;
  }

  if (lastIndex < expression.length) {
    tokens.push({ type: "text", value: expression.slice(lastIndex) });
  }
  return tokens;
};

/** $Value is always available — it refers to the raster pixel value */
const VALUE_FIELD: Field = { name: "Value" };

/** $Area is always available — it refers to the area covered by each pixel/cell */
const AREA_FIELD: Field = { name: "Area" };

interface FormulaInputProps {
  value: string;
  onChange: (expression: string) => void;
  fields: Field[];
  placeholder?: string;
  disabled?: boolean;
  externalError?: string;
}

export const FormulaInput: React.FC<FormulaInputProps> = ({
  value,
  onChange,
  fields,
  placeholder,
  disabled,
  externalError
}) => {
  const [focused, setFocused] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const blurTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const tokens = useMemo(() => parseExpression(value), [value]);

  const usedVariables = useMemo(
    () => new Set(tokens.filter((t) => t.type === "variable").map((t) => t.value)),
    [tokens]
  );

  // Map of lowercase variable → properly-cased variable for normalization and validation.
  // $Value and $Area are always included regardless of the fields list.
  const fieldVarMap = useMemo(() => {
    const map = new Map<string, string>();
    fields.forEach((f) => {
      const v = toVariable(f.name);
      map.set(v.toLowerCase(), v);
    });
    map.set("$value", "$Value");
    map.set("$area", "$Area");
    return map;
  }, [fields]);

  // Validation: find variables not in the available fields (only show when blurred).
  // Comparison is case-insensitive so that casing errors are corrected on blur, not flagged.
  const unknownVariables = useMemo(() => {
    return tokens
      .filter((t) => t.type === "variable" && !fieldVarMap.has(t.value.toLowerCase()))
      .map((t) => toDisplayName(t.value));
  }, [tokens, fieldVarMap]);

  const hasError =
    !!externalError || (!focused && value.length > 0 && unknownVariables.length > 0);

  /** Extract the word fragment being typed at the cursor position */
  const updateSearchTerm = useCallback((inputValue: string, cursorPos: number) => {
    const textBeforeCursor = inputValue.slice(0, cursorPos);
    // Don't match if cursor is inside a $variable token
    const lastDollar = textBeforeCursor.lastIndexOf("$");
    if (lastDollar !== -1) {
      const potentialVar = inputValue
        .slice(lastDollar)
        .match(/^\$[A-Za-z_][A-Za-z0-9_/]*/);
      if (potentialVar && lastDollar + potentialVar[0].length >= cursorPos) {
        setSearchTerm("");
        return;
      }
    }
    const match = textBeforeCursor.match(/[a-zA-Z_][a-zA-Z0-9_/]*$/);
    setSearchTerm(match ? match[0] : "");
  }, []);

  const filteredFields = useMemo(() => {
    // Always include $Value. Only include $Area for calculation maps (when fields exist).
    const allFields =
      fields.length > 0 ? [VALUE_FIELD, AREA_FIELD, ...fields] : [VALUE_FIELD, ...fields];
    // Deduplicate by name (keep first occurrence)
    const seen = new Set<string>();
    const unique = allFields.filter((f) => {
      if (seen.has(f.name)) return false;
      seen.add(f.name);
      return true;
    });
    const term = searchTerm.toLowerCase();
    return term ? unique.filter((f) => f.name.toLowerCase().includes(term)) : unique;
  }, [fields, searchTerm]);

  const handleInsertVariable = useCallback(
    (fieldName: string) => {
      const variable = toVariable(fieldName);
      const input = inputRef.current;
      if (!input) return;

      const cursorPos = input.selectionStart ?? value.length;
      const insertStart = searchTerm
        ? Math.max(0, cursorPos - searchTerm.length)
        : cursorPos;

      const before = value.slice(0, insertStart);
      const after = value.slice(cursorPos);
      const needsLeadingSpace = before.length > 0 && !before.endsWith(" ");
      const needsTrailingSpace = after.length === 0 || !after.startsWith(" ");

      const insertText =
        (needsLeadingSpace ? " " : "") + variable + (needsTrailingSpace ? " " : "");
      const newValue = before + insertText + after;
      const newCursorPos = before.length + insertText.length;

      onChange(newValue);
      setSearchTerm("");

      setTimeout(() => {
        input.focus();
        input.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    },
    [value, onChange, searchTerm]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      const cursorPos = e.target.selectionStart ?? newValue.length;
      onChange(newValue);
      updateSearchTerm(newValue, cursorPos);
    },
    [onChange, updateSearchTerm]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter" && searchTerm && filteredFields.length > 0) {
        e.preventDefault();
        handleInsertVariable(filteredFields[0].name);
      }
    },
    [searchTerm, filteredFields, handleInsertVariable]
  );

  const handleFocus = useCallback(() => {
    if (!disabled) {
      clearTimeout(blurTimerRef.current);
      setFocused(true);
      setDropdownOpen(true);
      // Defer search term update so cursor position is settled
      setTimeout(() => {
        const input = inputRef.current;
        if (input) {
          updateSearchTerm(
            valueRef.current,
            input.selectionStart ?? valueRef.current.length
          );
        }
      }, 0);
    }
  }, [disabled, updateSearchTerm]);

  const handleBlur = useCallback(() => {
    blurTimerRef.current = setTimeout(() => {
      if (containerRef.current?.contains(document.activeElement)) return;
      setFocused(false);
      setDropdownOpen(false);
      setSearchTerm("");
      // Normalize variable casing: $net_pay → $Net_Pay
      const normalized = valueRef.current.replace(
        VARIABLE_REGEX,
        (match) => fieldVarMap.get(match.toLowerCase()) ?? match
      );
      if (normalized !== valueRef.current) onChange(normalized);
    }, 150);
  }, [fieldVarMap, onChange]);

  // Clear pending blur timer on unmount
  useEffect(() => {
    return () => clearTimeout(blurTimerRef.current);
  }, []);

  const handleContainerClick = useCallback(() => {
    if (disabled) return;
    // If already focused, let native click handle cursor placement
    if (focused) return;
    const input = inputRef.current;
    if (input) {
      input.focus();
    }
  }, [disabled, focused]);

  const handleInputClick = useCallback(() => {
    const input = inputRef.current;
    if (input) {
      updateSearchTerm(value, input.selectionStart ?? value.length);
    }
  }, [value, updateSearchTerm]);

  const fieldItems = useMemo(() => {
    if (filteredFields.length === 0) {
      return [
        {
          key: "no-results",
          label: <span className={styles.noResults}>{"No matching variables"}</span>,
          disabled: true
        }
      ];
    }
    return filteredFields.map((field) => ({
      key: field.name,
      label: (
        <Tag
          className={styles.dropdownTag}
          color={usedVariables.has(toVariable(field.name)) ? "blue" : "default"}>
          {field.name}
        </Tag>
      ),
      onClick: () => handleInsertVariable(field.name)
    }));
  }, [filteredFields, usedVariables, handleInsertVariable]);

  const renderDropdown = useCallback(
    (menu: React.ReactNode) => (
      <div className={styles.dropdownContent}>
        <span className={styles.dropdownTitle}>Available Variables</span>
        {menu}
      </div>
    ),
    []
  );

  // Close dropdown when clicking outside the component and dropdown
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current?.contains(target) ||
        (target instanceof Element && target.closest(`.${styles.dropdownOverlay}`))
      ) {
        return;
      }
      setDropdownOpen(false);
      setFocused(false);
      setSearchTerm("");
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [dropdownOpen]);

  const errorMessage = externalError
    ? externalError
    : hasError
    ? `Undefined Variable${
        unknownVariables.length > 1 ? "s" : ""
      }: ${unknownVariables.join(", ")}`
    : "";

  return (
    <div ref={containerRef} className={styles.formulaWrapper}>
      <Dropdown
        menu={{ items: fieldItems }}
        dropdownRender={renderDropdown}
        open={dropdownOpen && !disabled}
        overlayClassName={styles.dropdownOverlay}
        getPopupContainer={() => containerRef.current ?? document.body}>
        <div
          className={`${styles.formulaContainer} ${disabled ? styles.disabled : ""} ${
            hasError ? styles.error : ""
          }`}
          onClick={handleContainerClick}>
          <input
            ref={inputRef}
            className={`${styles.rawInput} ${!focused ? styles.hiddenInput : ""}`}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onClick={handleInputClick}
            placeholder={placeholder}
            aria-label={placeholder || "Formula input"}
            disabled={disabled}
          />
          {!focused && (
            <div className={styles.tokenDisplay}>
              {tokens.length === 0 ? (
                <span className={styles.placeholderText}>{placeholder}</span>
              ) : (
                tokens.map((token, i) =>
                  token.type === "variable" ? (
                    <Tag key={`${token.value}-${i}`} className={styles.variableTag}>
                      {toDisplayName(token.value)}
                    </Tag>
                  ) : (
                    <span key={`text-${i}`} className={styles.operatorText}>
                      {token.value}
                    </span>
                  )
                )
              )}
            </div>
          )}
          {hasError && (
            <Tooltip title={errorMessage}>
              <span className={styles.errorIcon}>
                <Icon path={mdiAlertCircle} size="16px" />
              </span>
            </Tooltip>
          )}
        </div>
      </Dropdown>
    </div>
  );
};
